import { useCallback, useState } from "react";
import { Auth } from "shared/@auth/Auth";
import { useQuery, useMutation } from "react-query";

export function useDreamStream(
  iboNumber,
  didCreateDreamStreamAccount,
  didRefetch,
  didRetrieveAccessCode
) {
  const { makeAuthedRequest } = Auth.useContainer();

  const { data, error, loading } = useQuery(
    // Query Key for invalidating data
    ["DreamStreamUser", iboNumber],
    () =>
      makeAuthedRequest(`/v1/dreamstream/user/${iboNumber}`).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          return { iboA: null, iboB: null };
        }
      })
  );

  const addDreamStreamAccount = useCallback(
    async (contact) => {
      return makeAuthedRequest(`/v1/dreamstream/user/create`, {
        method: "POST",
        body: JSON.stringify({
          iboNumber: contact.iboNumber,
          wwgId: contact.wwgId,
          name: contact.name,
          email: contact.email,
        }),
      }).then(async (res) => {
        if (res.status === 201) {
          didCreateDreamStreamAccount(contact);
        }
      });
    },
    [makeAuthedRequest]
  );

  const refetch = useCallback(
    async (contact) => {
      makeAuthedRequest(`/v1/dreamstream/user/${contact.iboNumber}`)
        .then(async (res) => {
          if (res.ok) {
            return res.json();
          } else {
            const body = await res.json();
            throw new Error(body.message);
          }
        })
        .then((data) => {
          if (!data) return;

          if (contact.iboInfo.type === "IBO-A") {
            didRefetch(data.iboA);
          } else {
            didRefetch(data.iboB);
          }
        });
    },
    [makeAuthedRequest]
  );

  const syncDreamStreamAccount = useCallback(
    async (contact) => {
      return makeAuthedRequest(`/v1/dreamstream/sync`, {
        method: "POST",
        body: JSON.stringify({
          type: "ibo",
          iboNumber: contact.iboNumber,
          wwgId: contact.wwgId,
        }),
      }).then(async (res) => {
        if (res.status !== 201) {
          console.log("ERROR CALLING API");
        }
      });
    },
    [makeAuthedRequest]
  );

  const fetchIboSyncData = useCallback(
    async (contact) => {
      return makeAuthedRequest(
        `/v1/dreamstream/sync/ibo/${contact.iboNumber}/${contact.wwgId}`
      )
        .then(async (res) => {
          if (res.ok) {
            return res.json();
          } else {
            const body = await res.json();
            throw new Error(body.message);
          }
        })
        .catch((error) => {
          console.error("Error fetching data: ", error);
        });
    },
    [makeAuthedRequest]
  );

  const syncOrganization = useCallback(
    async (syncObject) => {
      return makeAuthedRequest(`/v1/dreamstream/sync`, {
        method: "POST",
        body: JSON.stringify(syncObject),
      }).then(async (res) => {
        if (res.status !== 201) {
          return res.json();
        }
      });
    },
    [makeAuthedRequest]
  );

  const fetchGroupSyncData = useCallback(
    async (contact) => {
      return makeAuthedRequest(
        `/v1/dreamstream/sync/organization/${contact.organizationId}/${contact.pinLevel}`
      )
        .then(async (res) => {
          if (res.ok) {
            const data = await res.json();
            return data;
          } else {
            const body = await res.json();
            throw new Error(body.message);
          }
        })
        .catch((error) => {
          console.error("Error fetching data: ", error);
        });
    },
    [makeAuthedRequest]
  );

  const editDreamStreamName = useCallback(
    async (contact) => {
      return makeAuthedRequest(`/v1/dreamstream/user/name`, {
        method: "PUT",
        body: JSON.stringify({
          iboNumber: contact.iboNumber,
          wwgId: contact.wwgId,
          name: contact.name,
        }),
      }).then(async (res) => {
        if (res.ok) {
          return "OK"; //res.json();
        } else {
          return "ERROR";
        }
      });
    },
    [makeAuthedRequest]
  );

  const editDreamStreamEmail = useCallback(
    async (contact) => {
      return makeAuthedRequest(`/v1/dreamstream/user/email`, {
        method: "PUT",
        body: JSON.stringify({
          iboNumber: contact.iboNumber,
          wwgId: contact.wwgId,
          email: contact.email,
        }),
      }).then(async (res) => {
        if (res.ok) {
          return "OK"; //res.json();
        } else {
          if (res.status === 409) {
            return "DUPLICATE";
          } else {
            return "ERROR";
          }
        }
      });
    },
    [makeAuthedRequest]
  );

  const editDreamStreamNameAndEmail = useCallback(
    async (contact) => {
      return makeAuthedRequest(`/v1/dreamstream/user/update`, {
        method: "PUT",
        body: JSON.stringify({
          iboNumber: contact.iboNumber,
          wwgId: contact.wwgId,
          name: contact.name,
          email: contact.email,
        }),
      }).then(async (res) => {
        if (res.ok) {
          return "OK"; //res.json();
        } else {
          if (res.status === 409) {
            return "DUPLICATE";
          } else {
            return "ERROR";
          }
        }
      });
    },
    [makeAuthedRequest]
  );

  const retrieveAccessCode = useCallback(
    async (contact) => {
      return makeAuthedRequest(`/v1/dreamstream/user/accesscode`, {
        method: "PUT",
        body: JSON.stringify({
          iboNumber: contact.iboNumber,
          wwgId: contact.wwgId,
        }),
      })
        .then(async (res) => {
          if (res.ok) {
            return res.json();
          } else {
            return "ERROR";
          }
        })
        .then((data) => {
          if (!data) return;

          if (data.accessCode) {
            didRetrieveAccessCode(data.accessCode, contact);
          } else {
            didRetrieveAccessCode("ERROR", null);
          }
        });
    },
    [makeAuthedRequest]
  );

  const cancelSyncPut = useMutation(["cancelSync"], (syncId) =>
    makeAuthedRequest(`/v1/dreamstream/sync/complete`, {
      method: "PUT",
      body: JSON.stringify(syncId),
    }).then(async (res) => {
      if (res.ok) {
        return await res.json();
      } else {
        const body = await res.json();
        throw new Error(body.message);
      }
    })
  );

  const cancelHistoryPut = useMutation(
    ["cancelHistory"],
    ({ iboNumber, wwgId }) =>
      makeAuthedRequest(`/v1/dreamstream/history/complete`, {
        method: "PUT",
        body: JSON.stringify({ iboNumber, wwgId }),
      })
  );

  return {
    data,
    error,
    loading,

    addDreamStreamAccount,

    refetch,

    syncDreamStreamAccount,
    fetchIboSyncData,

    editDreamStreamName,
    editDreamStreamEmail,
    editDreamStreamNameAndEmail,

    syncOrganization,
    fetchGroupSyncData,

    retrieveAccessCode,

    cancelSyncPut,
    cancelHistoryPut,
  };
}
